import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faFileLines,
  faClipboardList,
  faMobileScreen,
  faComments,
  faBell,
  faLock,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';

type StatusVariant = 'configured' | 'not-configured' | 'empty' | 'coming-soon';

interface FeatureCardProps {
  icon: typeof faEnvelope;
  title: string;
  description: string;
  status: StatusVariant;
  comingSoon?: boolean;
  navigateTo?: string;
}

const STATUS_CONFIG: Record<StatusVariant, { label: string; className: string }> = {
  configured: {
    label: 'Configured',
    className: 'bg-green-500/10 text-green-700 dark:text-green-400',
  },
  'not-configured': {
    label: 'Not Configured',
    className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  },
  empty: {
    label: 'Empty',
    className: 'bg-muted text-muted-foreground',
  },
  'coming-soon': {
    label: 'Coming Soon',
    className: 'bg-primary/10 text-primary',
  },
};

function FeatureCard({
  icon,
  title,
  description,
  status,
  comingSoon = false,
  navigateTo,
}: FeatureCardProps) {
  const navigate = useNavigate();
  const statusCfg = STATUS_CONFIG[status];

  const handleOpen = () => {
    void toast.info(`${title} configuration will be available in an upcoming release.`);
  };

  // Fully clickable card — no Open button
  if (navigateTo) {
    return (
      <button
        type="button"
        onClick={() => navigate(navigateTo)}
        className="group relative flex w-full flex-col rounded-xl border border-border bg-card p-5 shadow-sm transition-all hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-left"
      >
        {/* Icon + title */}
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/20">
            <FontAwesomeIcon icon={icon} className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0 pt-1">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          </div>
        </div>

        {/* Status badge */}
        <div className="mb-3">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ${statusCfg.className}`}
          >
            {statusCfg.label}
          </span>
        </div>

        {/* Description */}
        <p className="mb-4 flex-1 text-sm text-muted-foreground">{description}</p>

        {/* Arrow CTA */}
        <div className="flex items-center gap-1 text-xs font-medium text-primary transition-transform group-hover:translate-x-0.5">
          Open
          <FontAwesomeIcon icon={faChevronRight} className="h-2.5 w-2.5" />
        </div>
      </button>
    );
  }

  // Coming soon / no navigation
  return (
    <div
      className={`relative flex flex-col rounded-xl border bg-card p-5 shadow-sm transition-colors ${
        comingSoon ? 'opacity-60' : 'border-border'
      }`}
    >
      {comingSoon && (
        <div className="absolute right-3 top-3">
          <FontAwesomeIcon icon={faLock} className="h-3.5 w-3.5 text-muted-foreground/60" />
        </div>
      )}

      {/* Icon + title */}
      <div className="mb-3 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <FontAwesomeIcon icon={icon} className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 pt-1">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
      </div>

      {/* Status badge */}
      <div className="mb-3">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ${statusCfg.className}`}
        >
          {statusCfg.label}
        </span>
      </div>

      {/* Description */}
      <p className="mb-5 flex-1 text-sm text-muted-foreground">{description}</p>

      {/* Action */}
      {comingSoon ? (
        <Button size="sm" disabled className="w-full cursor-not-allowed">
          Open
        </Button>
      ) : (
        <Button size="sm" variant="secondary" className="w-full" onClick={handleOpen}>
          Open
        </Button>
      )}
    </div>
  );
}

const FEATURES: FeatureCardProps[] = [
  {
    icon: faEnvelope,
    title: 'Email Gateway',
    description:
      'Configure SMTP and API providers for outbound email delivery with priority-based routing.',
    status: 'not-configured',
    navigateTo: ROUTES.SETTINGS_COMMUNICATION_EMAIL,
  },
  {
    icon: faFileLines,
    title: 'Email Templates',
    description:
      'Manage branded email templates for notifications, reports, and customer communications.',
    status: 'not-configured',
    navigateTo: ROUTES.SETTINGS_COMMUNICATION_TEMPLATES,
  },
  {
    icon: faClipboardList,
    title: 'Email Delivery Log',
    // Phase 12.7b Wave 2 (D-123). The previous copy promised "bounce reports",
    // which this product cannot produce — no bounce or webhook processing
    // exists anywhere. Corrected rather than carried forward: a settings card
    // must not advertise a capability the page cannot deliver.
    description: 'Review outgoing email delivery attempts and failure reasons. Read-only.',
    status: 'not-configured',
    navigateTo: ROUTES.SETTINGS_COMMUNICATION_EMAIL_LOGS,
  },
  {
    icon: faMobileScreen,
    title: 'SMS',
    description: 'Send SMS alerts and notifications via your preferred gateway.',
    status: 'coming-soon',
    comingSoon: true,
  },
  {
    icon: faComments,
    title: 'WhatsApp',
    description: 'Reach customers and technicians through WhatsApp Business API.',
    status: 'coming-soon',
    comingSoon: true,
  },
  {
    icon: faBell,
    title: 'Push Notifications',
    description: 'Deliver real-time push alerts to web and mobile clients.',
    status: 'coming-soon',
    comingSoon: true,
  },
];

export function CommunicationPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Communication Center"
        description="Configure how your organisation communicates with users and customers."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((feature) => (
          <FeatureCard key={feature.title} {...feature} />
        ))}
      </div>
    </div>
  );
}
